import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import { useAuth } from '../hooks/useAuth';
import SearchableSelect from './SearchableSelect';
import SalesReceiptPrint from './SalesReceiptPrint';
import { buildSalesEditDiff } from '../utils/salesEditDiff';
import { patientCardPath } from '../utils/patientCardLink';
import { formatSalesStatus, salesStatusClass, salesStatusKey } from '../utils/salesStatus';
import '../styles/SalesView.css';

const HEADER_EDIT_KEY = '/sales-transactions/edit-header';
const ITEMS_EDIT_KEY = '/sales-transactions/edit-items';
const OLDER_EDIT_KEY = '/sales-transactions/edit-older';
const OVERRIDE_PRICING_KEY = '/sales-transactions/override-pricing';
const EDIT_DISCOUNTS_KEY = '/sales-transactions/edit-discounts';
const AUDIT_VIEW_KEY = '/sales-transactions/audit';
const RECORD_PAYMENT_KEY = '/sales-transactions/record-payment';
const SALES_ADD_KEY = '/sales-transactions';
const MAX_LINE_DISCOUNT_PERCENT = 20;

const BRANCH_CASH_CUSTOMERS = {
  Pultney: 'Cash Payment Pultney',
  Kissy: 'CASH PAYMENT KISSY',
};

const DEFAULT_PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'banktransfer', label: 'Bank Transfer' },
  { value: 'check', label: 'Check' },
  { value: 'creditcard', label: 'Credit Card' },
  { value: 'others', label: 'Others' },
];

const formatPaymentMode = (mode) => {
  if (!mode) return '—';
  const found = DEFAULT_PAYMENT_MODES.find(
    (m) => m.value === String(mode).toLowerCase()
  );
  if (found) return found.label;
  return String(mode);
};

const emptyHeader = {
  sales_type: '',
  customer_name: '',
  patient_name: '',
  patient_id: '',
  patient_mobile: '',
  doctor_name: '',
  sales_date: '',
  sales_number: '',
  sales_total: '',
  sales_adjustments: '',
  zoho_id: '',
  sales_id: '',
  sales_status: '',
};

const salesApiBase = () =>
  `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_SALES_TRANSACTIONS}`;

const isOlderThanTwoDays = (salesDate) => {
  if (!salesDate) return true;
  const sale = new Date(`${String(salesDate).slice(0, 10)}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - sale.getTime()) / (24 * 60 * 60 * 1000));
  return diffDays > 2;
};

const formatMoney = (value) =>
  parseFloat(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Parse catalog prices that may be stored as "SLL750.00" / "SLL 0.00". */
const parseItemPrice = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const resolveCatalogRate = (option = {}) => {
  const candidates = [option.rate, option.raw?.item_price, option.item_price];
  for (const candidate of candidates) {
    const parsed = parseItemPrice(candidate);
    if (parsed != null) return parsed;
  }
  return 0;
};

const deriveRate = (item) => {
  const quantity = Number(item.quantity ?? 1);
  const parsedRate = parseItemPrice(item.rate);
  if (parsedRate != null) return parsedRate;
  // line_total is net of discount; add discount back to recover unit rate.
  if (quantity) {
    return (
      (Number(item.line_total ?? 0) + Number(item.discount_amount ?? 0)) / quantity
    );
  }
  return 0;
};

/** Convert stored monetary discount_amount → percentage for display/edit. */
const discountAmountToPercent = (discountAmount, rate, quantity) => {
  const gross = Number(rate || 0) * Number(quantity || 0);
  if (!gross) return 0;
  return Number(((Number(discountAmount || 0) / gross) * 100).toFixed(4));
};

/** Convert percentage → monetary discount_amount (decimal) for storage. */
const percentToDiscountAmount = (percent, rate, quantity) => {
  const gross = Number(rate || 0) * Number(quantity || 0);
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  return Number(((gross * pct) / 100).toFixed(2));
};

const computeLineTotal = (rate, quantity, discountAmount) =>
  Number(
    (Number(rate || 0) * Number(quantity || 0) - Number(discountAmount || 0)).toFixed(2)
  );

const normalizeLine = (item) => {
  const quantity = Number(item.quantity ?? 1) || 0;
  const rate = deriveRate(item);
  const discount_amount = Number(item.discount_amount ?? 0) || 0;
  return {
    sales_record_id: item.sales_record_id,
    line_number: item.line_number,
    line_description: item.line_description || '',
    quantity,
    rate,
    discount_amount,
    line_total:
      item.line_total != null && item.line_total !== ''
        ? Number(item.line_total)
        : computeLineTotal(rate, quantity, discount_amount),
    item_id: item.item_id || null,
  };
};

const SalesView = ({
  salesData,
  salesNumber,
  mode = 'view',
  initialSalesType = '',
  onCreated,
  onSaved,
  onEditingChange,
}) => {
  const { hasPermission } = useAuth();
  const isCreateMode = mode === 'create';
  const canAdd = hasPermission(SALES_ADD_KEY, 'Add');

  const [formData, setFormData] = useState({ header: { ...emptyHeader }, items: [] });
  const [editDraft, setEditDraft] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showAudit, setShowAudit] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [addItemQuery, setAddItemQuery] = useState('');
  const [createBranch, setCreateBranch] = useState('Pultney');
  const [payThroughAccount, setPayThroughAccount] = useState('');
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [paymentAccountsLoading, setPaymentAccountsLoading] = useState(false);
  const [paymentAccountsError, setPaymentAccountsError] = useState('');

  const [invoicePayments, setInvoicePayments] = useState([]);
  const [paymentsExpanded, setPaymentsExpanded] = useState(false);
  /** Zoho invoice balance from last Sync / Record Payment refresh (authoritative when set). */
  const [zohoBalance, setZohoBalance] = useState(null);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [recordPaymentLoading, setRecordPaymentLoading] = useState(false);
  const [recordPaymentSaving, setRecordPaymentSaving] = useState(false);
  const [paymentModes, setPaymentModes] = useState(DEFAULT_PAYMENT_MODES);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [depositAccountsLoading, setDepositAccountsLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    customer_name: '',
    customer_id: '',
    branch: 'Pultney',
    payment_date: '',
    payment_mode: 'cash',
    account_id: '',
    amount: '',
    balance: 0,
  });
  const [viewPayment, setViewPayment] = useState(null);
  const [viewPaymentLoading, setViewPaymentLoading] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [isPreparingDocument, setIsPreparingDocument] = useState(false);
  const [ageEditRestricted, setAgeEditRestricted] = useState(null);
  const [ageEditReason, setAgeEditReason] = useState('');
  const pdfMenuRef = useRef(null);
  const receiptPrintRef = useRef(null);

  const applyAgeEditPolicy = useCallback((data) => {
    if (data && typeof data.ageEditRestricted === 'boolean') {
      setAgeEditRestricted(data.ageEditRestricted);
      setAgeEditReason(data.ageEditReason || '');
    }
  }, []);

  const canEditHeader =
    (isCreateMode && canAdd) || hasPermission(HEADER_EDIT_KEY, 'Edit');
  const canEditItems =
    (isCreateMode && canAdd) || hasPermission(ITEMS_EDIT_KEY, 'Edit');
  const canEditOlder = hasPermission(OLDER_EDIT_KEY, 'Edit');
  const canOverridePricing = hasPermission(OVERRIDE_PRICING_KEY, 'Edit');
  const canEditDiscounts = hasPermission(EDIT_DISCOUNTS_KEY, 'Edit');
  const canViewAudit = !isCreateMode && hasPermission(AUDIT_VIEW_KEY, 'View');
  const canRecordPayment =
    !isCreateMode && hasPermission(RECORD_PAYMENT_KEY, 'Add');
  const isAgeRestricted =
    ageEditRestricted != null
      ? ageEditRestricted
      : isOlderThanTwoDays(formData.header.sales_date);
  const canStartEdit =
    !isCreateMode &&
    (canEditHeader || canEditItems) &&
    (!isAgeRestricted || canEditOlder);
  const isInvoice = (formData.header.sales_type || initialSalesType) === 'Invoice';
  const isSalesReceipt =
    (formData.header.sales_type || initialSalesType) === 'Sales Receipt';
  const canPdfPrint =
    !isCreateMode &&
    !isEditing &&
    Boolean(formData.header.sales_id) &&
    (isInvoice || isSalesReceipt);

  useEffect(() => {
    if (typeof onEditingChange === 'function') {
      onEditingChange(isEditing || isCreateMode);
    }
  }, [isEditing, isCreateMode, onEditingChange]);

  useEffect(() => {
    if (!showPdfMenu) return undefined;
    const onDocClick = (event) => {
      if (pdfMenuRef.current && !pdfMenuRef.current.contains(event.target)) {
        setShowPdfMenu(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setShowPdfMenu(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showPdfMenu]);

  const applyLoadedRecord = useCallback((header, items) => {
    const normalizedHeader = {
      ...emptyHeader,
      ...(header || {}),
      sales_date: header?.sales_date
        ? String(header.sales_date).slice(0, 10)
        : '',
    };
    setFormData({
      header: normalizedHeader,
      items: (items || []).map(normalizeLine),
    });
  }, []);

  const fetchSalesDetails = useCallback(
    async (salesId) => {
      const response = await axios.get(`${salesApiBase()}/${salesId}`);
      const headerItems = { ...(response.data.header?.[0] || response.data.header || {}) };
      applyLoadedRecord(headerItems, response.data.lineItems || []);
      applyAgeEditPolicy(response.data);
      return headerItems;
    },
    [applyLoadedRecord, applyAgeEditPolicy]
  );

  const fetchSalesDetailsByNumber = useCallback(
    async (number) => {
      const response = await axios.get(`${salesApiBase()}/bySalesNumber/${number}`);
      const headerItems = { ...(response.data.header || {}) };
      applyLoadedRecord(headerItems, response.data.lineItems || []);
      applyAgeEditPolicy(response.data);
      return headerItems;
    },
    [applyLoadedRecord, applyAgeEditPolicy]
  );

  const loadInvoicePayments = useCallback(async (salesId, salesType) => {
    if (!salesId || salesType !== 'Invoice') {
      setInvoicePayments([]);
      return;
    }
    try {
      const response = await axios.get(`${salesApiBase()}/${salesId}/payments`);
      setInvoicePayments(response.data.payments || []);
    } catch (error) {
      console.error('Error loading invoice payments:', error);
      setInvoicePayments([]);
    }
  }, []);

  const loadDepositAccounts = useCallback(async () => {
    setDepositAccountsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/bank-accounts/paid-through-accounts`
      );
      const accounts = response.data || [];
      setDepositAccounts(accounts);
      return accounts;
    } catch (error) {
      console.error('Error loading deposit accounts:', error);
      setDepositAccounts([]);
      return [];
    } finally {
      setDepositAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isCreateMode) {
      const today = new Date().toISOString().split('T')[0];
      const cashCustomer = BRANCH_CASH_CUSTOMERS.Pultney;
      const draft = {
        header: {
          ...emptyHeader,
          sales_type: initialSalesType || 'Invoice',
          sales_date: today,
          sales_adjustments: '0',
          customer_name:
            initialSalesType === 'Sales Receipt' ? cashCustomer : '',
        },
        items: [],
      };
      setFormData(draft);
      setEditDraft(draft);
      setIsEditing(true);
      setCreateBranch('Pultney');
      setPayThroughAccount('');
      setInvoicePayments([]);
      setZohoBalance(null);
      setShowRecordPayment(false);
      setViewPayment(null);
      setStatusMessage('Fill in the details and save to create this record.');
      setErrorMessage('');
      return;
    }

    const load = async () => {
      setErrorMessage('');
      try {
        let header = null;
        if (salesData && salesData.sales_id) {
          header = await fetchSalesDetails(salesData.sales_id);
        } else if (salesNumber) {
          header = await fetchSalesDetailsByNumber(salesNumber);
        }
        if (header?.sales_id) {
          await loadInvoicePayments(header.sales_id, header.sales_type);
        }
        setZohoBalance(null);
      } catch (error) {
        console.error('Error fetching sale details:', error);
        setErrorMessage('Could not load the sales record.');
      }
    };
    load();
  }, [
    isCreateMode,
    initialSalesType,
    salesData,
    salesNumber,
    fetchSalesDetails,
    fetchSalesDetailsByNumber,
    loadInvoicePayments,
  ]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [doctorsRes, customersRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_GET_DOCTORS}`
          ),
          axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/customers`, {
            params: { status: 'Active' },
          }),
        ]);
        setDoctors(doctorsRes.data.doctors || doctorsRes.data || []);
        setCustomers(customersRes.data || []);
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    if (!isCreateMode || initialSalesType !== 'Sales Receipt') {
      setPaymentAccounts([]);
      setPayThroughAccount('');
      return;
    }

    const loadAccounts = async () => {
      setPaymentAccountsLoading(true);
      setPaymentAccountsError('');
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/bank-accounts/pos-accounts`,
          { params: { branch: createBranch } }
        );
        const accounts = response.data || [];
        setPaymentAccounts(accounts);
        setPayThroughAccount((prev) =>
          accounts.some((a) => a.account_number === prev)
            ? prev
            : accounts[0]?.account_number || ''
        );
      } catch (error) {
        console.error('Error loading payment accounts:', error);
        setPaymentAccounts([]);
        setPayThroughAccount('');
        setPaymentAccountsError('Failed to load payment accounts.');
      } finally {
        setPaymentAccountsLoading(false);
      }
    };
    loadAccounts();
  }, [isCreateMode, initialSalesType, createBranch]);

  const getBranch = (number) => {
    if (!number) return '';
    return String(number).startsWith('K-') ? 'Kissy Street' : 'Pultney Street';
  };

  const handleCreateBranchChange = (branch) => {
    setCreateBranch(branch);
    if (isCreateMode && editDraft?.header?.sales_type === 'Sales Receipt') {
      const cashName = BRANCH_CASH_CUSTOMERS[branch] || '';
      const current = editDraft.header.customer_name;
      const wasCash =
        !current || Object.values(BRANCH_CASH_CUSTOMERS).includes(current);
      if (wasCash) {
        setEditDraft((prev) => ({
          ...prev,
          header: { ...prev.header, customer_name: cashName },
        }));
      }
    }
  };

  const searchCatalogItems = async (query) => {
    if (!query || query.trim().length < 2) {
      setItemOptions([]);
      return;
    }
    setItemSearchLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/items`,
        { params: { name: query.trim() } }
      );
      setItemOptions(
        (response.data || []).map((item) => {
          const rate = parseItemPrice(item.item_price) ?? 0;
          return {
            value: item.item_id,
            label: item.item_name,
            meta: formatMoney(rate),
            rate,
            raw: item,
          };
        })
      );
    } catch (error) {
      console.error('Item search failed:', error);
      setItemOptions([]);
    } finally {
      setItemSearchLoading(false);
    }
  };

  const handleBeginEdit = async () => {
    if (!formData.header.sales_id) return;
    setIsLoading(true);
    setStatusMessage('Refreshing from Zoho…');
    setErrorMessage('');
    try {
      const response = await axios.post(
        `${salesApiBase()}/${formData.header.sales_id}/begin-edit`
      );
      applyLoadedRecord(response.data.header, response.data.lineItems);
      applyAgeEditPolicy(response.data);
      setEditDraft({
        header: {
          ...emptyHeader,
          ...response.data.header,
          sales_date: response.data.header?.sales_date
            ? String(response.data.header.sales_date).slice(0, 10)
            : '',
        },
        items: (response.data.lineItems || []).map(normalizeLine),
      });
      setIsEditing(true);
      setStatusMessage('Record refreshed. You can edit now.');
    } catch (error) {
      console.error('begin-edit failed:', error);
      setErrorMessage(
        error.response?.data?.error || 'Could not refresh this record from Zoho.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncFromZoho = async () => {
    if (!formData.header.sales_id || isEditing || isCreateMode) return;
    setIsSyncing(true);
    setErrorMessage('');
    setStatusMessage('Syncing from Zoho…');
    try {
      const response = await axios.post(
        `${salesApiBase()}/${formData.header.sales_id}/sync`
      );
      applyLoadedRecord(response.data.header, response.data.lineItems || []);
      if (Array.isArray(response.data.payments)) {
        setInvoicePayments(response.data.payments);
      } else {
        const header = response.data.header || {};
        if (header.sales_id) {
          await loadInvoicePayments(header.sales_id, header.sales_type);
        }
      }
      if (response.data.balance != null && response.data.balance !== '') {
        setZohoBalance(Number(response.data.balance));
      } else {
        setZohoBalance(null);
      }
      setStatusMessage('Synced from Zoho.');
      // Refresh list only — do not remount this view (sales_id may change after
      // sync and would wipe amount_applied by reloading local payments).
      if (typeof onSaved === 'function') {
        onSaved({ ...response.data, _syncOnly: true });
      }
    } catch (error) {
      console.error('sync from Zoho failed:', error);
      setErrorMessage(
        error.response?.data?.error || error.message || 'Could not sync from Zoho.'
      );
      setStatusMessage('');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCancelEdit = () => {
    if (isCreateMode) {
      setErrorMessage('');
      setStatusMessage('Create cancelled. Close this panel or choose Add again.');
      return;
    }
    setIsEditing(false);
    setEditDraft(null);
    setItemOptions([]);
    setAddItemQuery('');
    setStatusMessage('');
    setErrorMessage('');
  };

  const handleBeginRecordPayment = async () => {
    if (!formData.header.sales_id || !isInvoice) return;
    setRecordPaymentLoading(true);
    setErrorMessage('');
    setStatusMessage('Refreshing payments from Zoho…');
    setShowAudit(false);
    setViewPayment(null);
    try {
      const [beginRes, accounts] = await Promise.all([
        axios.post(`${salesApiBase()}/${formData.header.sales_id}/begin-record-payment`),
        loadDepositAccounts(),
      ]);
      const data = beginRes.data;
      if (data.header) {
        applyLoadedRecord(data.header, data.lineItems || []);
      }
      setInvoicePayments(data.payments || []);
      setPaymentModes(data.payment_modes || DEFAULT_PAYMENT_MODES);
      if (data.balance != null && data.balance !== '') {
        setZohoBalance(Number(data.balance));
      }
      const defaults = data.defaults || {};
      const defaultAccount = accounts[0]?.account_number || '';
      setPaymentForm({
        customer_name: defaults.customer_name || data.header?.customer_name || '',
        customer_id: data.customer_id || '',
        branch: defaults.branch || 'Pultney',
        payment_date: defaults.payment_date || new Date().toISOString().slice(0, 10),
        payment_mode: defaults.payment_mode || 'cash',
        account_id: defaultAccount,
        amount:
          defaults.amount != null && defaults.amount !== ''
            ? String(defaults.amount)
            : String(data.balance ?? 0),
        balance: Number(data.balance ?? 0),
      });
      setShowRecordPayment(true);
      setStatusMessage(
        Number(data.balance ?? 0) <= 0
          ? 'Invoice has no remaining balance.'
          : 'Payments refreshed. Enter payment details.'
      );
    } catch (error) {
      console.error('begin record payment failed:', error);
      setErrorMessage(
        error.response?.data?.error || error.message || 'Could not start record payment.'
      );
      setStatusMessage('');
    } finally {
      setRecordPaymentLoading(false);
    }
  };

  const handleSaveRecordPayment = async () => {
    if (!formData.header.sales_id) return;
    const amountNum = Number(paymentForm.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setErrorMessage('Amount must be greater than zero.');
      return;
    }
    if (amountNum > Number(paymentForm.balance) + 0.009) {
      setErrorMessage(
        `Amount cannot exceed remaining balance (${formatMoney(paymentForm.balance)}).`
      );
      return;
    }
    if (!paymentForm.payment_mode) {
      setErrorMessage('Payment mode is required.');
      return;
    }
    if (!paymentForm.account_id) {
      setErrorMessage('Deposit To is required.');
      return;
    }
    if (!paymentForm.payment_date) {
      setErrorMessage('Payment date is required.');
      return;
    }

    setRecordPaymentSaving(true);
    setErrorMessage('');
    setStatusMessage('Recording payment…');
    try {
      const response = await axios.post(
        `${salesApiBase()}/${formData.header.sales_id}/payments`,
        {
          amount: amountNum,
          payment_mode: paymentForm.payment_mode,
          payment_date: paymentForm.payment_date,
          account_id: paymentForm.account_id,
          branch: paymentForm.branch,
          customer_id: paymentForm.customer_id || undefined,
        }
      );
      if (response.data.header) {
        applyLoadedRecord(response.data.header, response.data.lineItems || []);
      }
      setInvoicePayments(response.data.payments || []);
      if (response.data.balance != null && response.data.balance !== '') {
        setZohoBalance(Number(response.data.balance));
      }
      setShowRecordPayment(false);
      setStatusMessage('Payment recorded successfully.');
      if (typeof onSaved === 'function') {
        onSaved(response.data);
      }
    } catch (error) {
      console.error('record payment failed:', error);
      setErrorMessage(
        error.response?.data?.error || error.message || 'Failed to record payment.'
      );
      setStatusMessage('');
    } finally {
      setRecordPaymentSaving(false);
    }
  };

  const handleViewPayment = async (paymentId) => {
    if (!formData.header.sales_id || !paymentId) return;
    setShowRecordPayment(false);
    setShowAudit(false);
    setViewPaymentLoading(true);
    setViewPayment({ payment_id: paymentId });
    setErrorMessage('');
    try {
      const response = await axios.get(
        `${salesApiBase()}/${formData.header.sales_id}/payments/${paymentId}`
      );
      setViewPayment(response.data.payment || null);
    } catch (error) {
      console.error('view payment failed:', error);
      setErrorMessage(
        error.response?.data?.error || error.message || 'Could not load payment.'
      );
      setViewPayment(null);
    } finally {
      setViewPaymentLoading(false);
    }
  };

  const validateDraft = () => {
    if (!String(editDraft?.header?.patient_name || '').trim()) {
      return 'Patient Name is required.';
    }
    if (!String(editDraft?.header?.patient_id || '').trim()) {
      return 'Patient ID is required.';
    }
    if (!String(editDraft?.header?.doctor_name || '').trim()) {
      return 'Doctor is required.';
    }
    if (!String(editDraft?.header?.sales_date || '').trim()) {
      return 'Sales Date is required.';
    }
    if (isCreateMode && !createBranch) {
      return 'Branch is required.';
    }
    if (
      isCreateMode &&
      editDraft?.header?.sales_type === 'Invoice' &&
      !String(editDraft?.header?.customer_name || '').trim()
    ) {
      return 'Customer is required for Invoices.';
    }
    if (
      isCreateMode &&
      editDraft?.header?.sales_type === 'Sales Receipt' &&
      !payThroughAccount
    ) {
      return 'Payment account is required for Sales Receipts.';
    }
    if (!editDraft?.items?.length) {
      return 'At least one line item is required.';
    }
    if (editDraft.items.some((item) => !item.item_id)) {
      return 'Each line item must be selected from the catalog.';
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateDraft();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    if (isCreateMode) {
      setIsSaving(true);
      setErrorMessage('');
      setStatusMessage('Creating…');
      try {
        const response = await axios.post(salesApiBase(), {
          header: {
            ...editDraft.header,
            sales_type: initialSalesType || editDraft.header.sales_type,
          },
          items: editDraft.items,
          branch: createBranch,
          pay_through_account:
            editDraft.header.sales_type === 'Sales Receipt'
              ? payThroughAccount
              : null,
        });
        applyLoadedRecord(response.data.header, response.data.lineItems);
        setIsEditing(false);
        setEditDraft(null);
        setStatusMessage('Sales record created.');
        if (typeof onCreated === 'function') {
          onCreated(response.data);
        }
      } catch (error) {
        console.error('Create failed:', error);
        setErrorMessage(
          error.response?.data?.error || 'Could not create the sales record.'
        );
        setStatusMessage('');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!editDraft?.header?.sales_id) return;
    setIsSaving(true);
    setErrorMessage('');
    setStatusMessage('Saving…');
    try {
      const response = await axios.put(
        `${salesApiBase()}/${editDraft.header.sales_id}`,
        {
          header: editDraft.header,
          items: editDraft.items,
        }
      );
      applyLoadedRecord(response.data.header, response.data.lineItems);
      setIsEditing(false);
      setEditDraft(null);
      setStatusMessage('Sales record saved.');
      if (typeof onSaved === 'function') {
        onSaved(response.data);
      }
    } catch (error) {
      console.error('Save failed:', error);
      setErrorMessage(
        error.response?.data?.error || 'Could not save the sales record.'
      );
      setStatusMessage('');
    } finally {
      setIsSaving(false);
    }
  };

  const updateDraftHeader = (field, value) => {
    setEditDraft((prev) => ({
      ...prev,
      header: { ...prev.header, [field]: value },
    }));
  };

  const updateDraftItem = (index, field, value) => {
    setEditDraft((prev) => {
      const items = [...prev.items];
      const next = { ...items[index], [field]: value };
      const quantity = Number(next.quantity ?? 1) || 0;
      const rate = Number(next.rate ?? 0);
      let discountAmount = Number(next.discount_amount ?? 0) || 0;

      if (field === 'discount_percent') {
        const cappedPercent = Math.max(
          0,
          Math.min(MAX_LINE_DISCOUNT_PERCENT, Number(value) || 0)
        );
        discountAmount = percentToDiscountAmount(cappedPercent, rate, quantity);
        next.discount_amount = discountAmount;
        delete next.discount_percent;
      } else if (field === 'quantity' || field === 'rate') {
        // Keep the same discount % when qty/rate changes; recompute amount.
        const prevGross =
          Number(items[index].rate ?? 0) * Number(items[index].quantity ?? 0);
        const prevPct =
          prevGross > 0
            ? (Number(items[index].discount_amount ?? 0) / prevGross) * 100
            : 0;
        discountAmount = percentToDiscountAmount(prevPct, rate, quantity);
        next.discount_amount = discountAmount;
      }

      next.line_total = computeLineTotal(rate, quantity, discountAmount);
      items[index] = next;
      return { ...prev, items };
    });
  };

  const selectLineItem = (index, option) => {
    setEditDraft((prev) => {
      const items = [...prev.items];
      const existing = items[index];
      const quantity = Number(existing.quantity ?? 1) || 1;
      const isExisting = existing.sales_record_id != null;
      // Existing lines without override keep current rate (Zoho price list applies on save).
      // New lines use catalog price as a local estimate only (rate is not sent to Zoho).
      const rate =
        isExisting && !canOverridePricing
          ? Number(existing.rate ?? deriveRate(existing))
          : resolveCatalogRate(option);
      const prevGross =
        Number(existing.rate ?? 0) * Number(existing.quantity ?? 0);
      const prevPct =
        prevGross > 0
          ? (Number(existing.discount_amount ?? 0) / prevGross) * 100
          : 0;
      const discount_amount = percentToDiscountAmount(prevPct, rate, quantity);
      items[index] = {
        ...existing,
        item_id: option.value,
        line_description: option.label,
        rate,
        discount_amount,
        line_total: computeLineTotal(rate, quantity, discount_amount),
      };
      return { ...prev, items };
    });
  };

  const removeDraftItem = (index) => {
    setEditDraft((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const addItemFromCatalog = (option) => {
    const rate = resolveCatalogRate(option);
    setEditDraft((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          sales_record_id: null,
          line_number: prev.items.length + 1,
          line_description: option.label,
          quantity: 1,
          rate,
          line_total: Number(rate.toFixed(2)),
          item_id: option.value,
          discount_amount: 0,
        },
      ],
    }));
    setAddItemQuery('');
    setItemOptions([]);
  };

  const loadAudit = async () => {
    if (!formData.header.sales_id) return;
    setShowAudit(true);
    setAuditLoading(true);
    try {
      const response = await axios.get(
        `${salesApiBase()}/${formData.header.sales_id}/audit`
      );
      setAuditEntries(response.data.entries || []);
    } catch (error) {
      console.error('Audit load failed:', error);
      setErrorMessage(
        error.response?.data?.error || 'Could not load the audit trail.'
      );
    } finally {
      setAuditLoading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const printPdfBlob = (blob) =>
    new Promise((resolve, reject) => {
      const pdfBlob =
        blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'Print document');
      iframe.style.position = 'fixed';
      iframe.style.left = '0';
      iframe.style.top = '0';
      iframe.style.width = '100vw';
      iframe.style.height = '100vh';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      iframe.style.zIndex = '-1';
      let printed = false;
      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        iframe.remove();
        URL.revokeObjectURL(url);
      };
      const triggerPrint = () => {
        if (printed) return;
        printed = true;
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          resolve();
        } catch (err) {
          reject(err);
        } finally {
          setTimeout(cleanup, 60000);
        }
      };
      iframe.onload = () => setTimeout(triggerPrint, 400);
      document.body.appendChild(iframe);
      iframe.src = url;
      setTimeout(triggerPrint, 1500);
    });

  const receiptPdfOptions = {
    margin: 0.4,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
    pagebreak: { mode: 'avoid-all' },
  };

  const buildReceiptPdfBlob = async () => {
    const el = receiptPrintRef.current;
    if (!el) {
      throw new Error('Receipt template is not ready.');
    }
    const host = el.closest('.sales-receipt-print-host');
    host?.classList.add('is-capturing');
    try {
      return await html2pdf().from(el).set(receiptPdfOptions).outputPdf('blob');
    } finally {
      host?.classList.remove('is-capturing');
    }
  };

  const parseBlobError = async (data) => {
    if (!(data instanceof Blob)) {
      return data?.error || 'Could not prepare the document.';
    }
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.error || 'Could not prepare the document.';
    } catch (_) {
      return 'Could not prepare the document.';
    }
  };

  const fetchInvoicePdfBlob = async () => {
    const response = await axios.get(
      `${salesApiBase()}/${formData.header.sales_id}/invoice-pdf`,
      { responseType: 'blob', timeout: 60000 }
    );
    const blob = response.data;
    if (blob.type && blob.type.includes('json')) {
      throw new Error(await parseBlobError(blob));
    }
    return blob;
  };

  const handlePdfPrint = async (action) => {
    setShowPdfMenu(false);
    if (!formData.header.sales_id) return;
    setErrorMessage('');
    setStatusMessage('Preparing…');
    setIsPreparingDocument(true);
    const filename = `${formData.header.sales_number || 'document'}.pdf`;
    try {
      if (isInvoice) {
        const blob = await fetchInvoicePdfBlob();
        if (action === 'pdf') {
          downloadBlob(blob, filename);
        } else {
          await printPdfBlob(blob);
        }
      } else if (isSalesReceipt) {
        const blob = await buildReceiptPdfBlob();
        if (action === 'pdf') {
          downloadBlob(blob, filename);
        } else {
          await printPdfBlob(blob);
        }
      }
      setStatusMessage('');
    } catch (error) {
      console.error('PDF/Print failed:', error);
      let message = error.message || 'Could not prepare the document.';
      if (error.response?.data) {
        message = await parseBlobError(error.response.data);
      }
      setStatusMessage('');
      setErrorMessage(message);
    } finally {
      setIsPreparingDocument(false);
    }
  };

  const display = isEditing && editDraft ? editDraft : formData;
  const computedSubtotal = (display.items || []).reduce(
    (sum, item) => sum + Number(item.line_total || 0),
    0
  );
  const computedTotal =
    computedSubtotal + Number(display.header.sales_adjustments || 0);
  const viewTotal = Number(display.header.sales_total || 0);
  const isPaidStatus = salesStatusKey(display.header.sales_status) === 'paid';
  const currentSalesNumber = String(display.header.sales_number || '').trim();

  /** Per-invoice applied amount when known; else full amount only if payment is sole invoice. */
  const resolveAppliedAmount = (payment) => {
    if (payment.amount_applied != null && payment.amount_applied !== '') {
      const n = Number(payment.amount_applied);
      return Number.isFinite(n) ? n : null;
    }
    const invoices = String(payment.invoice_numbers || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (
      currentSalesNumber &&
      invoices.length === 1 &&
      invoices[0] === currentSalesNumber
    ) {
      return Number(payment.amount || 0);
    }
    return null;
  };

  const appliedAmounts = invoicePayments.map(resolveAppliedAmount);
  const hasReliableApplied = appliedAmounts.some((a) => a != null);
  const paymentsApplied = hasReliableApplied
    ? appliedAmounts.reduce((sum, a) => sum + (a != null ? a : 0), 0)
    : 0;
  const balanceDue = isPaidStatus
    ? 0
    : hasReliableApplied
      ? Math.max(0, viewTotal - paymentsApplied)
      : zohoBalance != null && Number.isFinite(zohoBalance)
        ? Math.max(0, zohoBalance)
        : viewTotal;

  const paymentDisplayAmount = (payment) => {
    const applied = resolveAppliedAmount(payment);
    if (applied != null) return applied;
    if (isPaidStatus && invoicePayments.length === 1) {
      return viewTotal;
    }
    // Multi-invoice payment without applied amount — do not show full payment total.
    const invoices = String(payment.invoice_numbers || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (invoices.length > 1) return null;
    return Number(payment.amount || 0);
  };
  const formatPaymentAmount = (payment) => {
    const amt = paymentDisplayAmount(payment);
    return amt == null ? '—' : formatMoney(amt);
  };
  const paymentsDisplayTotal = invoicePayments.reduce((sum, payment) => {
    const amt = paymentDisplayAmount(payment);
    return sum + (amt != null ? amt : 0);
  }, 0);
  const showViewSummaryExtras = !isCreateMode && !isEditing;
  const showPaymentsSection =
    showViewSummaryExtras && isInvoice && invoicePayments.length > 0;

  const doctorOptions = [
    { value: 'No Doctor', label: 'No Doctor' },
    ...doctors.map((doc) => ({
      value: doc.name || doc.doctor_name,
      label: doc.name || doc.doctor_name,
    })),
  ];

  const customerOptions = customers.map((customer) => ({
    value: customer.customer_name,
    label: customer.customer_name,
  }));

  return (
    <div className="sales-view-layout">
      <div className="sales-view-container">
        {formatSalesStatus(display.header.sales_status) && (
          <div
            className="sales-status-ribbon-clip"
            role="status"
            aria-label={`Status: ${formatSalesStatus(display.header.sales_status)}`}
          >
            <div
              className={`sales-status-ribbon sales-status-ribbon--${salesStatusClass(display.header.sales_status)}`}
            >
              <span>{formatSalesStatus(display.header.sales_status)}</span>
            </div>
          </div>
        )}
        <div className="sales-view-toolbar">
          <div className="sales-view-toolbar-actions">
            {!isCreateMode && !isEditing && formData.header.sales_id && (
              <button
                type="button"
                className="sales-view-btn sales-view-btn-icon"
                onClick={handleSyncFromZoho}
                disabled={isSyncing || isLoading || recordPaymentLoading}
                title="Sync from Zoho"
                aria-label="Sync from Zoho"
              >
                <svg
                  className={`sales-sync-icon${isSyncing ? ' spinning' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                  <path d="M16 16h5v5" />
                </svg>
                {isSyncing ? 'Syncing…' : 'Sync'}
              </button>
            )}
            {canPdfPrint && (
              <div className="sales-pdf-print no-print" ref={pdfMenuRef}>
                <button
                  type="button"
                  className="sales-view-btn sales-view-btn-icon"
                  onClick={() => setShowPdfMenu((open) => !open)}
                  disabled={isPreparingDocument || isLoading || isSyncing}
                  aria-haspopup="menu"
                  aria-expanded={showPdfMenu}
                  title="PDF / Print"
                >
                  {isPreparingDocument ? 'Preparing…' : 'PDF/Print'}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {showPdfMenu && (
                  <div className="sales-pdf-print-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handlePdfPrint('pdf')}
                      disabled={isPreparingDocument}
                    >
                      PDF
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handlePdfPrint('print')}
                      disabled={isPreparingDocument}
                    >
                      Print
                    </button>
                  </div>
                )}
              </div>
            )}
            {!isCreateMode && !isEditing && canStartEdit && (
              <button
                type="button"
                className="sales-view-btn primary"
                onClick={handleBeginEdit}
                disabled={isLoading || isSyncing}
              >
                {isLoading ? 'Refreshing…' : 'Edit'}
              </button>
            )}
            {!isCreateMode &&
              !isEditing &&
              (canEditHeader || canEditItems) &&
              isAgeRestricted &&
              !canEditOlder && (
              <span className="sales-view-hint">
                {ageEditReason ||
                  'This record is older than 2 days. Older-record edit permission is required.'}
              </span>
            )}
            {isEditing && (
              <>
                <button
                  type="button"
                  className="sales-view-btn primary"
                  onClick={handleSave}
                  disabled={isSaving || isLoading || (isCreateMode && !canAdd)}
                >
                  {isSaving ? (isCreateMode ? 'Creating…' : 'Saving…') : isCreateMode ? 'Create' : 'Save'}
                </button>
                {!isCreateMode && (
                  <button
                    type="button"
                    className="sales-view-btn"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                )}
              </>
            )}
            {canViewAudit && (
              <button
                type="button"
                className="sales-view-btn"
                onClick={loadAudit}
                disabled={auditLoading}
              >
                {showAudit ? 'Refresh Audit' : 'Audit Trail'}
              </button>
            )}
            {!isCreateMode &&
              !isEditing &&
              isInvoice &&
              canRecordPayment && (
              <button
                type="button"
                className="sales-view-btn primary"
                onClick={handleBeginRecordPayment}
                disabled={recordPaymentLoading || isLoading || isSyncing}
              >
                {recordPaymentLoading ? 'Refreshing…' : 'Record Payment'}
              </button>
            )}
          </div>
          {statusMessage && <div className="sales-view-status">{statusMessage}</div>}
          {errorMessage && <div className="sales-view-error">{errorMessage}</div>}
        </div>

        {showPaymentsSection && (
          <div className="sales-payments-received">
            <button
              type="button"
              className="sales-payments-received-toggle"
              onClick={() => setPaymentsExpanded((open) => !open)}
              aria-expanded={paymentsExpanded}
            >
              <span>Payments Received</span>
              <span className="sales-payments-badge">{invoicePayments.length}</span>
              <span className="sales-payments-chevron" aria-hidden="true">
                {paymentsExpanded ? '▾' : '▸'}
              </span>
            </button>
            {paymentsExpanded && (
              <ul className="sales-payments-list">
                {invoicePayments.map((payment) => (
                  <li key={payment.payment_id}>
                    <button
                      type="button"
                      className="sales-payments-list-item"
                      onClick={() => handleViewPayment(payment.payment_id)}
                    >
                      <span className="sales-payments-list-main">
                        {payment.payment_number
                          ? `#${payment.payment_number}`
                          : payment.payment_id}
                        {' · '}
                        {payment.date
                          ? String(payment.date).slice(0, 10)
                          : '—'}
                      </span>
                      <span className="sales-payments-list-meta">
                        {formatPaymentMode(payment.payment_mode)} ·{' '}
                        {formatPaymentAmount(payment)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="invoice-header">
          <div className="invoice-title">
            <h2>{display.header.sales_type || initialSalesType}</h2>
            <h3>{display.header.sales_number || (isCreateMode ? '(New)' : '')}</h3>
          </div>
          <div className="invoice-details">
            {isEditing && canEditHeader ? (
              <div className="sales-view-fields-grid">
                <div className="sales-view-field">
                  <label className="required">Sales Date *</label>
                  <input
                    type="date"
                    value={display.header.sales_date || ''}
                    onChange={(e) => updateDraftHeader('sales_date', e.target.value)}
                    required
                  />
                </div>
                <div className="sales-view-field">
                  <label className="required">Patient Name *</label>
                  <input
                    type="text"
                    value={display.header.patient_name || ''}
                    onChange={(e) => updateDraftHeader('patient_name', e.target.value)}
                    required
                  />
                </div>
                <div className="sales-view-field">
                  <label className="required">Patient ID *</label>
                  <input
                    type="text"
                    value={display.header.patient_id || ''}
                    onChange={(e) => updateDraftHeader('patient_id', e.target.value)}
                    required
                  />
                  {patientCardPath(display.header.patient_id) && (
                    <Link
                      className="sales-patient-link"
                      to={patientCardPath(display.header.patient_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open patient card
                    </Link>
                  )}
                </div>
                <div className="sales-view-field">
                  <label>Patient Mobile</label>
                  <input
                    type="text"
                    value={display.header.patient_mobile || ''}
                    onChange={(e) => updateDraftHeader('patient_mobile', e.target.value)}
                  />
                </div>
                <SearchableSelect
                  label="Doctor"
                  required
                  value={display.header.doctor_name || ''}
                  options={doctorOptions}
                  placeholder="Select doctor…"
                  onChange={(opt) => updateDraftHeader('doctor_name', opt.value)}
                />
                <div className="sales-view-field">
                  <label className={isCreateMode ? 'required' : undefined}>
                    Branch{isCreateMode ? ' *' : ''}
                  </label>
                  {isCreateMode ? (
                    <select
                      value={createBranch}
                      onChange={(e) => handleCreateBranchChange(e.target.value)}
                    >
                      <option value="Pultney">Pultney</option>
                      <option value="Kissy">Kissy</option>
                    </select>
                  ) : (
                    <div className="sales-view-readonly">
                      {getBranch(display.header.sales_number)}
                    </div>
                  )}
                </div>
                {(display.header.sales_type === 'Invoice' ||
                  display.header.sales_type === 'Sales Receipt') && (
                  <SearchableSelect
                    label="Customer"
                    required={display.header.sales_type === 'Invoice'}
                    value={display.header.customer_name || ''}
                    options={customerOptions}
                    placeholder="Select customer…"
                    onChange={(opt) => updateDraftHeader('customer_name', opt.value)}
                  />
                )}
              </div>
            ) : (
              <>
                <div>
                  <strong>Invoice Date:</strong>{' '}
                  {display.header.sales_date
                    ? new Date(display.header.sales_date).toLocaleDateString()
                    : ''}
                </div>
                <div>
                  <strong>Patient Name:</strong> {display.header.patient_name}
                </div>
                <div>
                  <strong>Patient ID:</strong>{' '}
                  {patientCardPath(display.header.patient_id) ? (
                    <Link
                      className="sales-patient-link"
                      to={patientCardPath(display.header.patient_id)}
                    >
                      {display.header.patient_id}
                    </Link>
                  ) : (
                    display.header.patient_id
                  )}
                </div>
                <div>
                  <strong>Patient Mobile:</strong> {display.header.patient_mobile}
                </div>
                <div>
                  <strong>Doctor:</strong> {display.header.doctor_name || '—'}
                </div>
                <div>
                  <strong>Branch:</strong> {getBranch(display.header.sales_number)}
                </div>
                {(display.header.sales_type === 'Invoice' ||
                  display.header.sales_type === 'Sales Receipt') && (
                  <div>
                    <strong>Customer:</strong> {display.header.customer_name}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="invoice-items">
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Quantity</th>
                <th>Rate</th>
                <th>Discount %</th>
                <th>Amount</th>
                {isEditing && canEditItems && <th />}
              </tr>
            </thead>
            <tbody>
              {(display.items || []).map((item, index) => {
                const discountPercent = discountAmountToPercent(
                  item.discount_amount,
                  item.rate ?? deriveRate(item),
                  item.quantity
                );
                return (
                <tr key={item.sales_record_id || `new-${index}`}>
                  <td className="sv-desc-cell">
                    {isEditing && canEditItems ? (
                      <SearchableSelect
                        value={item.item_id || ''}
                        displayLabel={item.line_description || ''}
                        options={
                          item.item_id
                            ? [
                                {
                                  value: item.item_id,
                                  label: item.line_description,
                                  rate:
                                    itemOptions.find(
                                      (opt) => String(opt.value) === String(item.item_id)
                                    )?.rate ?? item.rate,
                                },
                                ...itemOptions.filter(
                                  (opt) => String(opt.value) !== String(item.item_id)
                                ),
                              ]
                            : itemOptions
                        }
                        placeholder="Select item…"
                        loading={itemSearchLoading}
                        onSearch={searchCatalogItems}
                        onChange={(opt) => selectLineItem(index, opt)}
                        emptyMessage="Type to search items"
                      />
                    ) : (
                      item.line_description
                    )}
                  </td>
                  <td>
                    {isEditing && canEditItems ? (
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={item.quantity ?? 1}
                        onChange={(e) =>
                          updateDraftItem(index, 'quantity', e.target.value)
                        }
                      />
                    ) : (
                      item.quantity || 1
                    )}
                  </td>
                  <td>
                    {isEditing && canEditItems ? (
                      (() => {
                        const canEditRate =
                          canOverridePricing &&
                          (isCreateMode || item.sales_record_id != null);
                        return (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate ?? 0}
                            disabled={!canEditRate}
                            title={
                              !isCreateMode && item.sales_record_id == null
                                ? 'New lines use the Zoho price list; rate is set after save'
                                : !canOverridePricing
                                  ? 'Override Pricing permission required to change rate'
                                  : undefined
                            }
                            onChange={(e) =>
                              updateDraftItem(index, 'rate', e.target.value)
                            }
                          />
                        );
                      })()
                    ) : (
                      formatMoney(item.rate ?? deriveRate(item))
                    )}
                  </td>
                  <td>
                    {isEditing && canEditItems ? (
                      <div className="sales-view-percent-input">
                        <input
                          type="number"
                          min="0"
                          max={MAX_LINE_DISCOUNT_PERCENT}
                          step="0.01"
                          value={discountPercent}
                          disabled={!canEditDiscounts}
                          title={
                            !canEditDiscounts
                              ? 'Edit Discounts permission required to change discount'
                              : `Maximum discount is ${MAX_LINE_DISCOUNT_PERCENT}%`
                          }
                          onChange={(e) =>
                            updateDraftItem(index, 'discount_percent', e.target.value)
                          }
                        />
                        <span aria-hidden="true">%</span>
                      </div>
                    ) : (
                      `${Number(discountPercent.toFixed(2))}%`
                    )}
                  </td>
                  <td>{formatMoney(item.line_total)}</td>
                  {isEditing && canEditItems && (
                    <td>
                      <button
                        type="button"
                        className="sales-view-btn danger"
                        onClick={() => removeDraftItem(index)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              );
              })}
            </tbody>
          </table>

          {isEditing && canEditItems && (
            <div className="sales-view-add-item">
              <SearchableSelect
                label="Add item"
                value=""
                options={itemOptions}
                placeholder="Search catalog item…"
                loading={itemSearchLoading}
                onSearch={(q) => {
                  setAddItemQuery(q);
                  searchCatalogItems(q);
                }}
                onChange={addItemFromCatalog}
                emptyMessage={
                  addItemQuery.trim().length < 2
                    ? 'Type at least 2 characters to search'
                    : 'No matches'
                }
              />
            </div>
          )}
          {isEditing && canEditItems && canEditDiscounts && (
            <p className="sales-view-hint">
              Line discounts cannot be more than {MAX_LINE_DISCOUNT_PERCENT}%.
            </p>
          )}
        </div>

        <div className="invoice-summary">
          <div className="sales-summary-row">
            <strong>Subtotal:</strong>
            <span>{formatMoney(computedSubtotal)}</span>
          </div>
          {isEditing && canEditHeader && canOverridePricing ? (
            <div className="sales-view-field">
              <label>Adjustments</label>
              <input
                type="number"
                step="0.01"
                value={display.header.sales_adjustments ?? 0}
                onChange={(e) => updateDraftHeader('sales_adjustments', e.target.value)}
              />
            </div>
          ) : (
            <div className="sales-summary-row">
              <strong>Adjustments:</strong>
              <span>
                {formatMoney(display.header.sales_adjustments)}
                {isEditing && canEditHeader && !canOverridePricing && (
                  <span className="sales-view-hint">
                    {' '}
                    (Override Pricing permission required to change)
                  </span>
                )}
              </span>
            </div>
          )}
          <div className="sales-summary-row">
            <strong>Total:</strong>
            <span>
              {isEditing ? formatMoney(computedTotal) : formatMoney(display.header.sales_total)}
            </span>
          </div>
          {showViewSummaryExtras && invoicePayments.length > 0 && (
            <div className="sales-summary-payments">
              {invoicePayments.length === 1 ? (
                <button
                  type="button"
                  className="sales-summary-payment-row"
                  onClick={() => handleViewPayment(invoicePayments[0].payment_id)}
                >
                  <span>
                    Payment #
                    {invoicePayments[0].payment_number ||
                      invoicePayments[0].payment_id}
                    {invoicePayments[0].date
                      ? ` · ${String(invoicePayments[0].date).slice(0, 10)}`
                      : ''}
                  </span>
                  <span>{formatPaymentAmount(invoicePayments[0])}</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="sales-summary-payment-row"
                  onClick={() => setPaymentsExpanded(true)}
                >
                  <span>Payment Made ({invoicePayments.length})</span>
                  <span>{formatMoney(paymentsDisplayTotal)}</span>
                </button>
              )}
            </div>
          )}
          {showViewSummaryExtras && (
            <div className="sales-summary-row sales-summary-balance-due">
              <strong>Balance Due:</strong>
              <span>{formatMoney(balanceDue)}</span>
            </div>
          )}
        </div>

        {isCreateMode && display.header.sales_type === 'Sales Receipt' && (
          <div className="sales-view-payment-section">
            <h3>Payment</h3>
            <p className="sales-view-hint">
              Select the account this payment will be deposited to.
            </p>
            {paymentAccountsLoading ? (
              <div>Loading payment accounts…</div>
            ) : paymentAccountsError ? (
              <div className="sales-view-error">{paymentAccountsError}</div>
            ) : paymentAccounts.length === 0 ? (
              <div className="sales-view-error">
                No payment accounts are configured for POS transactions.
              </div>
            ) : (
              <div className="sales-view-field">
                <label className="required">Payment Account *</label>
                <select
                  value={payThroughAccount}
                  onChange={(e) => setPayThroughAccount(e.target.value)}
                  required
                >
                  <option value="">-- Select account --</option>
                  {paymentAccounts.map((account) => (
                    <option
                      key={account.account_number}
                      value={account.account_number}
                    >
                      {account.account_name}
                      {account.account_type ? ` (${account.account_type})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {showAudit && (
        <aside className="sales-audit-panel">
          <div className="sales-audit-panel-header">
            <h3>Audit Trail</h3>
            <button type="button" className="sales-view-btn" onClick={() => setShowAudit(false)}>
              Close
            </button>
          </div>
          {auditLoading ? (
            <p>Loading…</p>
          ) : auditEntries.length === 0 ? (
            <p>No audit entries for this sales record.</p>
          ) : (
            <ul className="sales-audit-list">
              {auditEntries.map((entry) => {
                const diff = buildSalesEditDiff(entry.old_data, entry.new_data);
                return (
                  <li key={entry.audit_id} className="sales-audit-entry">
                    <div className="sales-audit-meta">
                      <strong>Edit</strong>
                      {entry.sales_type ? ` · ${entry.sales_type}` : ''}
                    </div>
                    <div className="sales-audit-meta">
                      {entry.changed_at
                        ? new Date(entry.changed_at).toLocaleString()
                        : ''}{' '}
                      · by {entry.changed_by || '—'}
                    </div>

                    {!diff.hasChanges ? (
                      <p className="sales-audit-no-diff">No field differences recorded.</p>
                    ) : (
                      <div className="sales-audit-diff">
                        {diff.headerChanges.length > 0 && (
                          <div className="sales-audit-diff-section">
                            <div className="sales-audit-diff-title">Header</div>
                            <ul>
                              {diff.headerChanges.map((change) => (
                                <li key={change.label}>
                                  <span className="sales-audit-field">{change.label}:</span>{' '}
                                  <span className="sales-audit-from">{String(change.from)}</span>
                                  <span className="sales-audit-arrow"> → </span>
                                  <span className="sales-audit-to">{String(change.to)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {diff.lineChanges.length > 0 && (
                          <div className="sales-audit-diff-section">
                            <div className="sales-audit-diff-title">Line items</div>
                            <ul>
                              {diff.lineChanges.map((change, idx) => (
                                <li key={`${change.type}-${change.label}-${idx}`}>
                                  {change.type === 'added' && (
                                    <>
                                      <span className="sales-audit-badge added">Added</span>{' '}
                                      {change.label}
                                      {change.details?.length
                                        ? ` (${change.details.join(', ')})`
                                        : ''}
                                    </>
                                  )}
                                  {change.type === 'removed' && (
                                    <>
                                      <span className="sales-audit-badge removed">Removed</span>{' '}
                                      {change.label}
                                      {change.details?.length
                                        ? ` (${change.details.join(', ')})`
                                        : ''}
                                    </>
                                  )}
                                  {change.type === 'changed' && (
                                    <>
                                      <span className="sales-audit-badge changed">Changed</span>{' '}
                                      {change.label}
                                      <ul className="sales-audit-subchanges">
                                        {(change.fieldChanges || []).map((fc) => (
                                          <li key={fc.label}>
                                            <span className="sales-audit-field">{fc.label}:</span>{' '}
                                            <span className="sales-audit-from">
                                              {String(fc.from)}
                                            </span>
                                            <span className="sales-audit-arrow"> → </span>
                                            <span className="sales-audit-to">{String(fc.to)}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    <details className="sales-audit-raw">
                      <summary>Full snapshot</summary>
                      <pre>
                        {JSON.stringify(
                          { old: entry.old_data, new: entry.new_data },
                          null,
                          2
                        )}
                      </pre>
                    </details>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      )}

      {showRecordPayment && (
        <aside className="sales-audit-panel sales-payment-panel">
          <div className="sales-audit-panel-header">
            <h3>Record Payment</h3>
            <button
              type="button"
              className="sales-view-btn"
              onClick={() => setShowRecordPayment(false)}
              disabled={recordPaymentSaving}
            >
              Close
            </button>
          </div>
          <div className="sales-view-fields-grid sales-payment-form">
            <div className="sales-view-field">
              <label>Customer Name</label>
              <input type="text" value={paymentForm.customer_name || ''} readOnly />
            </div>
            <div className="sales-view-field">
              <label>Payment #</label>
              <input type="text" value="Auto-generated" readOnly />
            </div>
            <div className="sales-view-field">
              <label className="required">Branch *</label>
              <select
                value={paymentForm.branch}
                onChange={(e) =>
                  setPaymentForm((prev) => ({ ...prev, branch: e.target.value }))
                }
              >
                <option value="Pultney">Pultney Street</option>
                <option value="Kissy">Kissy Street</option>
              </select>
            </div>
            <div className="sales-view-field">
              <label className="required">Payment Date *</label>
              <input
                type="date"
                value={paymentForm.payment_date || ''}
                onChange={(e) =>
                  setPaymentForm((prev) => ({ ...prev, payment_date: e.target.value }))
                }
                required
              />
            </div>
            <div className="sales-view-field">
              <label className="required">Payment Mode *</label>
              <select
                value={paymentForm.payment_mode}
                onChange={(e) =>
                  setPaymentForm((prev) => ({ ...prev, payment_mode: e.target.value }))
                }
                required
              >
                {paymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sales-view-field">
              <label className="required">Deposit To *</label>
              {depositAccountsLoading ? (
                <p className="sales-view-hint">Loading accounts…</p>
              ) : (
                <select
                  value={paymentForm.account_id}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, account_id: e.target.value }))
                  }
                  required
                >
                  <option value="">-- Select account --</option>
                  {depositAccounts.map((account) => (
                    <option
                      key={account.account_number}
                      value={account.account_number}
                    >
                      {account.account_name}
                      {account.account_type ? ` (${account.account_type})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="sales-view-field">
              <label className="required">Amount *</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) =>
                  setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))
                }
                required
              />
              <p className="sales-view-hint">
                Remaining balance: {formatMoney(paymentForm.balance)}
              </p>
            </div>
          </div>
          <div className="sales-payment-form-actions">
            <button
              type="button"
              className="sales-view-btn primary"
              onClick={handleSaveRecordPayment}
              disabled={
                recordPaymentSaving ||
                depositAccountsLoading ||
                Number(paymentForm.balance) <= 0
              }
            >
              {recordPaymentSaving ? 'Saving…' : 'Save Payment'}
            </button>
          </div>
        </aside>
      )}

      {(viewPayment || viewPaymentLoading) && (
        <aside className="sales-audit-panel sales-payment-panel">
          <div className="sales-audit-panel-header">
            <h3>Payment</h3>
            <button
              type="button"
              className="sales-view-btn"
              onClick={() => setViewPayment(null)}
            >
              Close
            </button>
          </div>
          {viewPaymentLoading ? (
            <p>Loading…</p>
          ) : viewPayment ? (
            <div className="sales-view-fields-grid sales-payment-form">
              <div className="sales-view-field">
                <label>Customer Name</label>
                <input
                  type="text"
                  value={viewPayment.customer_name || ''}
                  readOnly
                />
              </div>
              <div className="sales-view-field">
                <label>Payment #</label>
                <input
                  type="text"
                  value={viewPayment.payment_number || viewPayment.payment_id || ''}
                  readOnly
                />
              </div>
              <div className="sales-view-field">
                <label>Branch</label>
                <input
                  type="text"
                  value={viewPayment.branch_name || viewPayment.location_name || '—'}
                  readOnly
                />
              </div>
              <div className="sales-view-field">
                <label>Payment Date</label>
                <input
                  type="text"
                  value={
                    viewPayment.date ? String(viewPayment.date).slice(0, 10) : ''
                  }
                  readOnly
                />
              </div>
              <div className="sales-view-field">
                <label>Payment Mode</label>
                <input
                  type="text"
                  value={formatPaymentMode(viewPayment.payment_mode)}
                  readOnly
                />
              </div>
              <div className="sales-view-field">
                <label>Deposit To</label>
                <input
                  type="text"
                  value={
                    viewPayment.account_name ||
                    viewPayment.deposit_to_account_name ||
                    viewPayment.account_id ||
                    '—'
                  }
                  readOnly
                />
              </div>
              <div className="sales-view-field">
                <label>Amount</label>
                <input
                  type="text"
                  value={formatMoney(viewPayment.amount)}
                  readOnly
                />
              </div>
              {viewPayment.invoice_numbers && (
                <div className="sales-view-field">
                  <label>Invoice(s)</label>
                  <input type="text" value={viewPayment.invoice_numbers} readOnly />
                </div>
              )}
            </div>
          ) : (
            <p>Payment not found.</p>
          )}
        </aside>
      )}
      {isSalesReceipt && !isCreateMode && (
        <div className="sales-receipt-print-host">
          <SalesReceiptPrint
            ref={receiptPrintRef}
            header={formData.header}
            items={formData.items}
            paymentMode="Cash"
          />
        </div>
      )}
    </div>
  );
};

export default SalesView;
